import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, X, MessageCircle } from 'lucide-react'
import { SEOHead } from '@/components/SEOHead'
import { ProductCard } from '@/components/products/ProductCard'
import { useChatBot } from '@/contexts/ChatBotContext'
import { productCategories, products } from '@/data/products'
import type { ProductCategory } from '@/data/products'
import type { SupportedLanguage } from '@/i18n'

export function ProductsPage() {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'it') as SupportedLanguage
  const [searchParams, setSearchParams] = useSearchParams()
  const { openWithMessage } = useChatBot()

  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ProductCategory | 'all'>(() => {
    const filterParam = searchParams.get('filter')
    if (filterParam && productCategories.some((c) => c.id === filterParam)) {
      return filterParam as ProductCategory
    }
    return 'all'
  })

  // Sync filter to URL query param
  useEffect(() => {
    if (activeFilter === 'all') {
      searchParams.delete('filter')
    } else {
      searchParams.set('filter', activeFilter)
    }
    setSearchParams(searchParams, { replace: true })
  }, [activeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Category filter
      if (activeFilter !== 'all' && product.category !== activeFilter) return false

      // Text search
      if (search.trim()) {
        const query = search.toLowerCase().trim()
        const name = product.name.toLowerCase()
        const description = t(product.descriptionKey).toLowerCase()
        if (!name.includes(query) && !description.includes(query)) return false
      }

      return true
    })
  }, [activeFilter, search, t])

  return (
    <>
      <SEOHead titleKey="seo.products.title" descriptionKey="seo.products.description" />

      {/* Hero */}
      <section className="relative bg-primary overflow-hidden pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="absolute inset-0">
          <img
            src="/media/products/products-hero.jpg"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/60 to-primary/90" />
        </div>

        <div className="container-main relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('products.heroTitle')}
            </h1>
            <p className="text-lg text-white/70">
              {t('products.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search + Filters + Grid */}
      <section className="section-padding bg-surface-light">
        <div className="container-main">
          {/* Search bar */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="max-w-xl mx-auto mb-8"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('products.searchPlaceholder')}
                className="w-full pl-12 pr-10 py-3.5 bg-white border border-surface-border rounded-xl text-text-body placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text-body transition-colors"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>

          {/* Category filter pills */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="flex flex-wrap justify-center gap-2 mb-6"
          >
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeFilter === 'all'
                  ? 'bg-accent text-white shadow-md'
                  : 'bg-white text-text-body border border-surface-border hover:border-accent/30 hover:text-accent'
              }`}
            >
              {t('products.filterAll')}
            </button>
            {productCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeFilter === cat.id
                    ? 'bg-accent text-white shadow-md'
                    : 'bg-white text-text-body border border-surface-border hover:border-accent/30 hover:text-accent'
                }`}
              >
                {t(cat.nameKey)}
              </button>
            ))}
          </motion.div>

          {/* Results counter */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="text-center text-sm text-text-muted mb-10"
          >
            {t('products.resultsCount', { count: filteredProducts.length })}
          </motion.p>

          {/* Product grid */}
          <AnimatePresence mode="wait">
            {filteredProducts.length > 0 ? (
              <motion.div
                key={`${activeFilter}-${search}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {filteredProducts.map((product, index) => {
                  const category = productCategories.find((c) => c.id === product.category)!
                  return (
                    <ProductCard
                      key={product.id}
                      product={product}
                      category={category}
                      lang={lang}
                      index={index}
                      showCategory
                    />
                  )
                })}

                {/* Filler cards — complete the last row (only when not searching) */}
                {!search.trim() && filteredProducts.length % 4 !== 0 &&
                  Array.from({ length: (4 - (filteredProducts.length % 4)) % 4 }).map((_, i) => (
                    <motion.div
                      key={`filler-${i}`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: (filteredProducts.length + i) * 0.05, duration: 0.4 }}
                    >
                      <button
                        onClick={() => openWithMessage(t('products.customProjectMessage'))}
                        className="group relative flex flex-col h-full bg-white/60 rounded-xl border-2 border-dashed border-surface-border overflow-hidden hover:border-accent/40 hover:bg-white transition-all text-left w-full"
                      >
                        <div className="aspect-[4/3] bg-surface-light/50 p-6 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-2xl bg-accent/8 flex items-center justify-center group-hover:bg-accent/15 transition-colors">
                            <MessageCircle className="w-8 h-8 text-accent/60 group-hover:text-accent transition-colors" />
                          </div>
                        </div>
                        <div className="p-5 flex flex-col flex-1">
                          <h3 className="font-semibold text-lg text-text-heading/70 group-hover:text-accent transition-colors mb-2">
                            {t('products.customProject')}
                          </h3>
                          <p className="text-sm text-text-muted/70 line-clamp-2 mb-4">
                            {t('products.customProjectDesc')}
                          </p>
                          <div className="mt-auto flex items-center text-sm font-medium text-accent/60 group-hover:text-accent transition-colors">
                            {t('products.customProjectCta')}
                            <MessageCircle className="w-4 h-4 ml-1" />
                          </div>
                        </div>
                      </button>
                    </motion.div>
                  ))}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center py-20"
              >
                <Search className="w-12 h-12 text-text-muted/30 mx-auto mb-4" />
                <p className="text-lg font-semibold text-text-heading mb-2">
                  {t('products.noResults')}
                </p>
                <p className="text-text-muted">
                  {t('products.noResultsHint')}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </>
  )
}
